import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { GOAL_AMOUNT_MAX } from '../../../shared/model';
import { BaseMaskedInput } from '../../../shared/ui';

type ValueIntroFieldProps = {
  currencyPrefix: string;
  goalValueInput: string;
  onGoalValueChange: (nextUnmaskedValue: string) => void;
  thousandsSeparator: string;
};

const VALUE_INTRO_FIELD_ID = 'registration-goals-goal-value';

function createValueMaskBlocks(thousandsSeparator: string) {
  return {
    amount: {
      max: GOAL_AMOUNT_MAX,
      mask: Number,
      scale: 0,
      signed: false,
      thousandsSeparator,
      normalizeZeros: true,
      padFractionalZeros: false,
    },
  };
}

const Field = styled.div.attrs({
  'data-ui': 'registration-goals-value-intro-field',
})`
  width: ${({ theme }) => theme.scale.px(353)};
  display: grid;
  gap: ${({ theme }) => theme.scale.px(6)};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 600;
  line-height: 1.25;
`;

const Input = styled(BaseMaskedInput)`
  width: 100%;
  height: ${({ theme }) => theme.scale.px(56)};
  box-sizing: border-box;
  padding: ${({ theme }) => theme.scale.px(18)}
    ${({ theme }) => theme.scale.px(13)};
  border-radius: ${({ theme }) => theme.scale.px(10)};
  background: ${({ theme }) => theme.colors.surface.subtle};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 400;
  line-height: 1.25;

  &::placeholder {
    color: ${({ theme }) => theme.colors.border.emphasis};
  }
`;

export function ValueIntroField({
  currencyPrefix,
  goalValueInput,
  onGoalValueChange,
  thousandsSeparator,
}: ValueIntroFieldProps) {
  const { t } = useTranslation();
  const maskPattern = useMemo(
    () => `${currencyPrefix}amount`,
    [currencyPrefix],
  );
  const valueMaskBlocks = useMemo(
    () => createValueMaskBlocks(thousandsSeparator),
    [thousandsSeparator],
  );

  return (
    <Field>
      <Label htmlFor={VALUE_INTRO_FIELD_ID}>
        {t('registrationGoalValue.amountLabel')}
      </Label>
      <Input
        id={VALUE_INTRO_FIELD_ID}
        aria-label={t('registrationGoalValue.amountAria')}
        inputMode="numeric"
        mask={maskPattern}
        unmask={true}
        lazy={false}
        blocks={valueMaskBlocks}
        placeholder={t('registrationGoalValue.amountPlaceholder')}
        value={goalValueInput}
        onAccept={(_nextValue, maskRef) =>
          onGoalValueChange(String(maskRef.unmaskedValue))
        }
      />
    </Field>
  );
}
